import { useMemo, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Database, ArrowRight } from "lucide-react";
import dayjs from "dayjs";

import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import { ModuleTitleCard, ContainerCard } from "../../components/ui/cards";
import { MASTER_MIGRATION_LIST, MASTER_BUNDLE_LIST } from "../../constants/api";
import { BRANCH_FILTER_CHANGED_EVENT } from "../../utils/branchSession";
import { getMasterMigrationBySlug, getMigrationSampleUrl, masterMigrationTitle } from "./masterMigrationConfigs";
import { migrationBranchFilter, migrationBranchLabel, migrationBranchRequired } from "./migrationBranch";

const STATUS_STYLES = {
    completed: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
    pending: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
};

const isAdmin = () =>
    JSON.parse(sessionStorage.getItem("is_admin") || "false") ||
    JSON.parse(sessionStorage.getItem("is_super_admin") || "false");

const MasterMigrationList = () => {
    const navigate = useNavigate();
    const { slug } = useParams();
    const config = getMasterMigrationBySlug(slug);
    const [data, setData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);

    const admin = useMemo(isAdmin, []);
    const [branchFilter, setBranchFilter] = useState(() => migrationBranchFilter());
    const branchName = migrationBranchLabel();

    useEffect(() => {
        const sync = () => setBranchFilter(migrationBranchFilter());
        window.addEventListener(BRANCH_FILTER_CHANGED_EVENT, sync);
        return () => window.removeEventListener(BRANCH_FILTER_CHANGED_EVENT, sync);
    }, []);

    const openBatch = (record) =>
        navigate(`/datamigration/master/${slug}/batch/${record.id}`, {
            state: { status: record.status },
        });

    const columns = useMemo(
        () => [
            {
                accessor: "migration_date_time",
                Header: "Migrated Date & Time",
                sortable: true,
                Cell: ({ value }) =>
                    value ? dayjs(value).format("DD MMM YYYY • hh:mm A") : "-",
            },
            {
                accessor: "migrated_by",
                Header: "Migrated By",
                Cell: ({ value }) => value || "-",
            },
            {
                accessor: "count",
                Header: "Records",
                Cell: ({ value }) => `${value || 0}/100`,
            },
            {
                accessor: "status",
                Header: "Status",
                filterable: true,
                Cell: ({ value }) => {
                    const cls = STATUS_STYLES[value] ||
                        "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
                    return (
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${cls}`}>
                            {value || "-"}
                        </span>
                    );
                },
            },
            {
                accessor: "actions",
                Header: "Action",
                Cell: ({ row }) => (
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-7"
                        onClick={() => openBatch(row)}
                    >
                        {row.status === "completed" ? "View" : "Resume"}
                        <ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                ),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [slug]
    );

    const pageTitle = masterMigrationTitle(config);

    if (!config) {
        return (
            <ContainerCard>
                <p className="text-sm text-muted-foreground p-4">Unknown migration type.</p>
            </ContainerCard>
        );
    }

    if (!admin) {
        return (
            <div>
                <ModuleTitleCard Icon={Database} title={pageTitle} />
                <ContainerCard>
                    <p className="text-sm text-muted-foreground p-4">
                        You are not authorized to access data migration.
                    </p>
                </ContainerCard>
            </div>
        );
    }

    const needsBranch = migrationBranchRequired();
    const branchId = branchFilter.branch;

    return (
        <div>
            <ModuleTitleCard
                Icon={Database}
                title={pageTitle}
                subtitle={
                    branchName
                        ? `${config.subtitle} Batches for ${branchName} only.`
                        : `${config.subtitle} Select a branch before starting a migration.`
                }
                totalRecords={totalRecords}
                buttonEnable={!needsBranch}
                buttonName="New Migration"
                setOpenDialog={() => {
                    if (needsBranch) return;
                    navigate(`/datamigration/master/${slug}/new`);
                }}
            />

            {needsBranch && (
                <div className="mx-1 mb-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
                    Select a branch from the header or branch picker before creating or viewing migrations.
                </div>
            )}

            {getMigrationSampleUrl(config.type) && (
                <div className="px-1 pb-2">
                    <a
                        href={getMigrationSampleUrl(config.type)}
                        download
                        className="text-sm text-sidebar-primary hover:underline"
                    >
                        Download sample CSV with realistic test data
                    </a>
                </div>
            )}

            <ContainerCard>
                <Reactable
                    pagename={`master-migration-${slug}`}
                    columns={columns}
                    data={data}
                    setData={setData}
                    setTotalCount={setTotalRecords}
                    url={config.isBundle ? MASTER_BUNDLE_LIST : MASTER_MIGRATION_LIST}
                    filter={{
                        ...(config.isBundle ? {} : { migration_type: config.type }),
                        ...(branchId ? { branch: branchId } : {}),
                    }}
                    serverSide
                    isCSVRequired
                />
            </ContainerCard>
        </div>
    );
};

export default MasterMigrationList;
